import { Button } from '@bechtel/gds';
import styled, { css, keyframes } from 'styled-components';

export interface AIAssistantEmbeddedStyledProps {
  isOpen: boolean;
}

export interface AIAssistantButtonStyledProps {
  isCollapsed?: boolean;
  variant?: 'nav' | 'header' | 'fab';
}

// Overlay fade animation
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

// Subtle glow animation for the button
const glow = keyframes`
  0%, 100% {
    box-shadow: 0 0 8px rgba(0, 150, 255, 0.3);
  }
  50% {
    box-shadow: 0 0 16px rgba(0, 150, 255, 0.6);
  }
`;

export const AIAssistantEmbeddedOverlayStyled = styled.div<AIAssistantEmbeddedStyledProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 1199;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? 'visible' : 'hidden')};
  transition:
    opacity 0.3s ease,
    visibility 0.3s ease;
`;

export const AIAssistantEmbeddedContainerStyled = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  z-index: 1200;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

export const AIAssistantEmbeddedPanelStyled = styled.div<AIAssistantEmbeddedStyledProps>`
  position: absolute;
  top: 0;
  right: 0;
  height: 100vh;
  width: 580px;
  background-color: ${({ theme }) => theme.colors.white};
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  transform: translateX(100%);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-family:
    'Graphik',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif;

  ${({ isOpen }) =>
    isOpen &&
    css`
      transform: translateX(0);
    `}

  @media only screen and (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    width: 100vw;
  }
`;

export const AIAssistantEmbeddedHeaderStyled = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacings[500]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey[300]};
  flex-shrink: 0;

  ${({ theme }) => css`
    background-image: linear-gradient(
      194deg,
      ${theme.colors.bechtel.blue[800]} 0%,
      ${theme.colors.bechtel.blue[1000]} 100%
    );
  `}
`;

export const AIAssistantEmbeddedCloseButtonStyled = styled(Button)`
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

export const AIAssistantEmbeddedIframeContainerStyled = styled.div`
  flex: 1;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.grey[100]};
  position: relative;
`;

export const AIAssistantEmbeddedIframeStyled = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background-color: white;
`;

export const AIAssistantEmbeddedLoadingStyled = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.grey[100]};
`;

export const AIAssistantEmbeddedButtonStyled = styled.button<AIAssistantButtonStyledProps>`
  display: flex;
  align-items: center;
  justify-content: ${({ isCollapsed }) => (isCollapsed ? 'center' : 'flex-start')};
  width: 100%;
  padding: ${({ theme, isCollapsed }) =>
    isCollapsed ? theme.spacings[400] : `${theme.spacings[400]} ${theme.spacings[500]}`};
  border: none;
  background: linear-gradient(135deg, rgba(0, 150, 255, 0.15) 0%, rgba(0, 100, 200, 0.25) 100%);
  border-radius: ${({ theme }) => theme.spacings[300]};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: ${({ theme }) => theme.spacings[400]};
  animation: ${glow} 3s ease-in-out infinite;

  ${({ variant, theme }) =>
    variant === 'header' &&
    css`
      width: auto;
      padding: ${theme.spacings[300]} ${theme.spacings[400]};
      margin: 0;
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.3);
      animation: none;
    `}

  ${({ variant, theme }) =>
    variant === 'fab' &&
    css`
      width: 48px;
      height: 48px;
      padding: 0;
      margin: 0;
      border-radius: 50%;
      background: linear-gradient(
        194deg,
        ${theme.colors.bechtel.blue[800]} 0%,
        ${theme.colors.bechtel.blue[1000]} 100%
      );
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    `}

  &:hover {
    background: linear-gradient(135deg, rgba(0, 150, 255, 0.25) 0%, rgba(0, 100, 200, 0.35) 100%);
    transform: translateX(2px);
    animation: none;

    ${({ variant, theme }) =>
      variant === 'header' &&
      css`
        background: rgba(255, 255, 255, 0.1);
        transform: none;
      `}

    ${({ variant }) =>
      variant === 'fab' &&
      css`
        transform: scale(1.05);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
      `}
  }

  &:active {
    transform: scale(0.98);
  }

  &:focus {
    outline: 2px solid rgba(0, 150, 255, 0.5);
    outline-offset: 2px;
  }
`;
